"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AssetService = exports.indexAssetData = exports.flattenAssetData = void 0;
const types_1 = require("@shapeshiftoss/types");
const axios_1 = __importDefault(require("axios"));
const descriptions_json_1 = __importDefault(require("./descriptions.json"));
const GenerateAssetIcon_1 = require("./GenerateAssetIcon");
const generatedAssetData_json_1 = __importDefault(require("./generatedAssetData.json"));
const flattenAssetData = (assetData) => {
    const flatAssetData = [];
    for (const baseAsset of assetData) {
        const newAsset = { ...baseAsset };
        delete newAsset.tokens;
        flatAssetData.push(newAsset);
        if (baseAsset.tokens) {
            for (const tokenAsset of baseAsset.tokens) {
                flatAssetData.push({
                    ...tokenAsset,
                    chain: baseAsset.chain,
                    network: baseAsset.network,
                    slip44: baseAsset.slip44,
                    explorer: baseAsset.explorer,
                    explorerAddressLink: baseAsset.explorerAddressLink,
                    explorerTxLink: baseAsset.explorerTxLink
                });
            }
        }
    }
    return flatAssetData;
};
exports.flattenAssetData = flattenAssetData;
const getDataIndexKey = (chain, network, tokenId) => {
    return chain + '_' + network + (tokenId ? '_' + tokenId : '');
};
const indexAssetData = (flatAssetData) => {
    return flatAssetData.reduce((acc, val) => {
        acc[getDataIndexKey(val.chain, val.network, val.tokenId)] = val;
        return acc;
    }, {});
};
exports.indexAssetData = indexAssetData;
class AssetService {
    constructor(assetFileUrl) {
        this.assetFileUrl = assetFileUrl;
    }
    get isInitialized() {
        return Array.isArray(this.assetData) && Array.isArray(this.flatAssetData);
    }
    checkInitialized() {
        if (!this.isInitialized)
            throw new Error('Asset service not initialized');
    }
    /**
     * Get asset data from assetFileUrl and flatten it for easier use
     */
    async initialize() {
        try {
            if (!this.assetFileUrl)
                throw new Error('No assetFileUrl');
            const { data } = await axios_1.default.get(this.assetFileUrl);
            if (!Array.isArray(data)) {
                throw new Error(`Asset Initialize: Return value ${data} is not valid`);
            }
            this.assetData = data;
        }
        catch (err) {
            this.assetData = generatedAssetData_json_1.default;
        }
        this.flatAssetData = (0, exports.flattenAssetData)(this.assetData);
        this.indexedAssetData = (0, exports.indexAssetData)(this.flatAssetData);
    }
    /**
     * Get list of all assets on a given network (mainnet, ropsten, etc) or all assets across all networks
     * @param network mainnet, testnet, eth ropsten, etc
     * @returns base coins (ETH, BNB, etc...) along with their supported tokens in a flattened list
     */
    byNetwork(network) {
        this.checkInitialized();
        return network
            ? this.flatAssetData.filter((asset) => asset.network == network)
            : this.flatAssetData;
    }
    /**
     * Find an asset by chain, network and tokenId
     * @param chain blockchain to look up by (btc, eth, etc...)
     * @param network mainnet, testnet, eth ropsten, etc
     * @param tokenId token identifier (contract address on eth)
     * @returns First asset found
     */
    byTokenId({ chain, network, tokenId }) {
        this.checkInitialized();
        const index = getDataIndexKey(chain, network !== null && network !== void 0 ? network : types_1.NetworkTypes.MAINNET, tokenId);
        const result = this.indexedAssetData[index];
        if (!result) {
            throw new Error(`AssetService:byTokenId: could not find tokenId ${tokenId}`);
        }
        return result;
    }
    async description({ asset }) {
        var _a, _b, _c;
        const descriptions = descriptions_json_1.default;
        // Return overridden asset description if it exists and add isTrusted for description links
        if (descriptions[asset.assetId]) {
            return {
                description: descriptions[asset.assetId],
                isTrusted: true
            };
        }
        if (asset.dataSource !== types_1.AssetDataSource.CoinGecko) {
            return { description: '' };
        }
        const contractUrl = typeof asset.tokenId === 'string' ? `/contract/${(_a = asset.tokenId) === null || _a === void 0 ? void 0 : _a.toLowerCase()}` : '';
        const errorMessage = `AssetService:description: no description availble for ${asset.tokenId} on chain ${asset.chain}`;
        try {
            const { data } = await axios_1.default.get(`https://api.coingecko.com/api/v3/coins/${asset.chain}${contractUrl}`);
            return { description: (_c = (_b = data === null || data === void 0 ? void 0 : data.description) === null || _b === void 0 ? void 0 : _b.en) !== null && _c !== void 0 ? _c : '' };
        }
        catch (e) {
            throw new Error(errorMessage);
        }
    }
    async generateAssetIconBase64(identity, text, options) {
        return (0, GenerateAssetIcon_1.getRenderedIdenticonBase64)(identity, text, options);
    }
}
exports.AssetService = AssetService;
